/** The to-do list under a filing - pure, so it is tested without a DOM (ADR-0212 decision 6).
 *
 * **It is the filing's own checks, not a second list.** Each row is one failing or unrun check (or,
 * where a check has fix items, one item of it: a bill missing for a site and a month, a lease
 * question for a site, a figure waiting for a second person, a statement to write). Passed checks
 * are kept apart, behind the screen's "Show passed". Nothing is computed here that the server did
 * not say: the counts, the percentage and the verdict are the server's, and this file only chooses
 * which checks are rows and words them.
 *
 * `not_applicable` is neither a row nor a passed row: it is out of the count, so it is out of the
 * list, exactly as the server leaves it out of `total`.
 */

import type { FilingCheck, FilingChecks } from "../../api/filingChecks";
import { shortDate, shortMonthYear } from "../../lib/format";
import { BLOCKER_MAX_WORDS as BLOCKER_MAX, BLOCKER_MIN_WORDS as BLOCKER_MIN } from "../../components/ui/PathToFiling";
import { fixLinkFor } from "../filingChecksModel";
import type { FixLink } from "../filingChecksModel";

export interface TodoFix extends FixLink {
  /** Whether this reader's role may take the act - the server's answer, never ours. */
  permitted: boolean;
  /** The roles that may take it (the endpoint's own gate), so a reader who may not is told whom to ask. */
  roles?: readonly string[];
  /** Set when the act is done in a drawer on this filing rather than on another page: the fix is a
   * button, not a link. Only the approval request does this today. */
  opens?: "send_for_approval";
}

export interface TodoRow {
  id: string;
  /** What to do, in a few words: "January bill", "Answer the lease question". */
  what: string;
  /** What is wrong, as a short state ("No figures for this company and year"), for a check that
   * failed; "" otherwise. The server's words: a check's title states what holds when it passes. */
  reason: string;
  /** The check's own short action ("Get the figures re-approved"), three to six words, the server's. */
  action: string;
  /** The site the row is about, or "" when it is not about one. */
  site: string;
  /** The month or period the row is about, or "". */
  period: string;
  /** The path station the check belongs to ("Data in"). */
  station: string;
  stationKey: string;
  state: FilingCheck["state"];
  /** The check this row came from, so a row can be traced to the checks list. */
  checkKey: string;
  fix?: TodoFix;
}

export interface Todo {
  /** Failing or unrun checks, in path order, worst first within a station. */
  open: TodoRow[];
  /** Checks that held. Behind "Show passed". */
  passed: TodoRow[];
}

/** Worst first, and **a check that is waiting comes last**: it cannot be acted on until another check
 * passes, so it is never the thing to do next. With waiting ahead of advisory the "Next:" line said
 * "Get the figures re-approved" while "Send for approval", the act that unblocks it, sat below it. */
const WORST_FIRST: readonly string[] = ["blocking", "not_evaluated", "advisory", "waiting"];

function parseDay(iso: string): { y: number; m: number; d: number } | null {
  const hit = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!hit) return null;
  return { y: Number(hit[1]), m: Number(hit[2]), d: Number(hit[3]) };
}

function lastDayOf(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** A window as a person reads it: "Jan 2025" for a whole calendar month, else its two ends. Every
 * name and order comes from `lib/format` (the reader's own order, a month always a name), and the
 * dates are the server's ISO strings, so nothing is parsed through the local time zone. */
export function windowLabel(start: string, end: string): string {
  const a = parseDay(start);
  const b = parseDay(end);
  if (!a || !b) return `${start} to ${end}`;
  if (a.y === b.y && a.m === b.m && a.d === 1 && b.d === lastDayOf(b.y, b.m)) {
    return shortMonthYear(`${String(a.y)}-${String(a.m).padStart(2, "0")}`);
  }
  return `${shortDate(start)} to ${shortDate(end)}`;
}

/** The windows a census item lists, from its target's `missing_windows` (`start/end` pairs, comma
 * separated). A malformed pair is dropped; an item with none falls back to one row. */
export function windowsOf(target: Readonly<Record<string, string>>): string[] {
  const raw = target.missing_windows;
  if (!raw) return [];
  return raw
    .split(",")
    .map((pair) => pair.split("/"))
    .filter((parts): parts is [string, string] => parts.length === 2 && Boolean(parts[0]) && Boolean(parts[1]))
    .map(([start, end]) => windowLabel(start, end));
}

function rank(state: string): number {
  const at = WORST_FIRST.indexOf(state);
  return at === -1 ? WORST_FIRST.length : at;
}

const FIX_WHAT: Record<string, string> = {
  missing_data: "Bill missing",
  boundary_question: "Answer the lease question",
  document_to_review: "Review a document",
  difference_to_explain: "Explain a difference",
  statement_to_write: "Write a statement",
};

/** Checks that fail with no fix item of their own, and the place a person goes to take them. A
 * navigation to a queue every role may open, so `permitted` is true; it is not an act. */
const QUEUE_FIX: Readonly<Record<string, FixLink>> = {
  unaccepted_figure: { label: "Open the queue", to: "/approvals" },
  approval_not_stale: { label: "Open the queue", to: "/approvals" },
};

const wordCount = (text: string) => text.split(/\s+/).filter(Boolean).length;

/** The check's action in words a person can act on. The server's label when it sent one of three to
 * six words; otherwise the station and a plain phrase. **Never the check's title** (a pass condition,
 * read as a status) and **never a sentence cut off at some word**: that was the "Next: The approval
 * still stands over the" line. */
function actionOf(check: FilingCheck): string {
  const label = check.action_label.trim();
  if (wordCount(label) >= BLOCKER_MIN && wordCount(label) <= BLOCKER_MAX) return label;
  return `${check.part_label}: needs attention`;
}

/** The act of asking for approval is done in a drawer on the filing, not on the record page. */
const SEND_NOW: TodoFix = { label: "Send now", to: "", permitted: true, opens: "send_for_approval" };

/** The approval rows say where the sign-off is even while they wait (U2). Approvals are asked only after
 * the checks, so while one blocks, `approval_in_force` is *waiting* and has no fix item of its own, and
 * the Fix cell read "–" for the very step the person came to take. The link is a way to the filing's
 * sign-off on its record (where the route, the stage and the reviewer are shown), not an act: it is
 * `permitted` for every role that can read the filing, and the act itself is refused by the server. */
const SIGN_OFF_KINDS: ReadonlySet<string> = new Set(["approval_in_force", "approval_not_stale"]);

function signOffFix(filingId: string | undefined): FixLink | undefined {
  return filingId
    ? { label: "Open the sign-off", to: `/records/${filingId}`, tab: "workflow" }
    : { label: "Open the queue", to: "/approvals" };
}

function rowsOf(check: FilingCheck, filingId: string | undefined): TodoRow[] {
  const action = actionOf(check);
  const failing = check.state === "blocking" || check.state === "advisory";
  const reason = failing ? check.failing_reason : "";
  const base = {
    station: check.part_label,
    stationKey: check.part,
    state: check.state,
    checkKey: check.key,
    action,
    reason,
  };
  if (check.state === "passed") {
    return [{ ...base, id: check.key, what: check.title, site: "", period: "" }];
  }
  if (check.fix_items.length === 0) {
    // Waiting, not run, or failing with nothing to point at: one row, the check's action.
    const queue = QUEUE_FIX[check.kind];
    const link: FixLink | undefined =
      queue && failing ? queue : SIGN_OFF_KINDS.has(check.kind) ? signOffFix(filingId) : undefined;
    const fix: TodoFix | undefined = link ? { ...link, permitted: true } : undefined;
    return [{ ...base, id: check.key, what: action, site: "", period: "", ...(fix ? { fix } : {}) }];
  }
  const rows: TodoRow[] = [];
  for (const item of check.fix_items) {
    // A missing bill is put right at Upload (ADR-0212's flow of a bill); the older checks tab keeps
    // pointing at the document library, which is where it always went. Asking for approval is a
    // drawer on this filing: the person stays where the checks are.
    const link: TodoFix =
      item.action === "request_approval"
        ? { ...SEND_NOW }
        : item.action === "upload_document"
          ? { label: "Upload the bill", to: "/upload", permitted: true }
          : { ...fixLinkFor(item), permitted: true };
    const fix: TodoFix = { ...link, permitted: item.permitted, roles: item.permitted_roles };
    const site = item.site_name ?? "";
    const windows = item.kind === "missing_data" ? windowsOf(item.target) : [];
    if (windows.length > 0) {
      windows.forEach((period, at) => {
        rows.push({
          ...base,
          id: `${item.item_key}#${String(at)}`,
          what: FIX_WHAT.missing_data ?? "Bill missing",
          // The row already says what is wrong, for which site and which month.
          reason: "",
          site,
          period,
          fix,
        });
      });
    } else {
      rows.push({
        ...base,
        id: item.item_key,
        // An approval is not waited for in the passive: the row says what to do ("Send for approval").
        what: FIX_WHAT[item.kind] ?? action,
        site,
        period: "",
        fix,
      });
    }
  }
  return rows;
}

/** Split a filing's checks into the to-do list and the passed ones. */
export function buildTodo(
  checks: Pick<FilingChecks, "checks" | "parts"> & Partial<Pick<FilingChecks, "obligation_id">>,
): Todo {
  const stationOrder = new Map(checks.parts.map((part, at) => [part.part, at]));
  const orderOf = (key: string) => stationOrder.get(key) ?? stationOrder.size;
  const open: TodoRow[] = [];
  const passed: TodoRow[] = [];
  const sorted = [...checks.checks].sort(
    (a, b) => orderOf(a.part) - orderOf(b.part) || rank(a.state) - rank(b.state),
  );
  for (const check of sorted) {
    if (check.state === "not_applicable") continue;
    const rows = rowsOf(check, checks.obligation_id);
    if (check.state === "passed") passed.push(...rows);
    else open.push(...rows);
  }
  return { open, passed };
}

/** Once an approval request is open the act "Send for approval" is done: the row says who has it and
 * offers no second send. The check itself is still advisory (a request is not an approval, F07), so
 * only the words and the missing button change; the percentage and the verdict are the server's. */
export function withOpenRequest(rows: readonly TodoRow[], reviewer: string | undefined): TodoRow[] {
  if (!reviewer) return [...rows];
  return rows.map((row) => {
    if (row.fix?.opens !== "send_for_approval") return row;
    const { fix: _sent, ...rest } = row;
    void _sent;
    return { ...rest, what: "Waiting for approval", reason: `With ${reviewer}`, state: "waiting" };
  });
}

/** Three to six words naming the first thing in the way, for the path's "Next:" line. The first open
 * row is the blocker: the list is in path order with the worst state first, so row one is the item
 * the path's current station is waiting on. What to do comes first, then where, then when, and the
 * last parts are the ones dropped when the name would be too long. */
export function blockerText(open: readonly TodoRow[]): string | undefined {
  const first = open[0];
  if (!first) return undefined;
  const candidates = [
    [first.what, first.site, first.period],
    [first.what, first.site],
    [first.what],
    [first.action],
  ].map((parts) => parts.filter(Boolean).join(" "));
  const fits = candidates.find((text) => wordCount(text) >= BLOCKER_MIN && wordCount(text) <= BLOCKER_MAX);
  if (fits) return fits;
  // **Never a slice of something longer.** Cutting a sentence at six words printed "Next: The approval
  // still stands over the" (design review 2026-11-03). The station and a plain phrase is always four
  // words, whatever the station is called.
  return `${first.station}: needs attention`;
}

/** The way a role is told it cannot take a step, once, for a column, when the server named no role. */
export const STEP_NOT_YOURS = "Needs another role";

const ARTICLE: Readonly<Record<string, string>> = { admin: "an", approver: "an", auditor: "an" };

/** "Needs an Admin or an Approver": the roles that may take the step, in words (G-Wauditor-02: the cell
 * said "Needs another role" and an Auditor could not tell which). Falls back to the generic sentence
 * when the server named none. */
export function needsRoles(roles: readonly string[] | undefined): string {
  if (!roles || roles.length === 0) return STEP_NOT_YOURS;
  const named = roles.map((role) => `${ARTICLE[role] ?? "a"} ${role.charAt(0).toUpperCase()}${role.slice(1)}`);
  const last = named.pop();
  return `Needs ${named.length > 0 ? `${named.join(", ")} or ` : ""}${last ?? ""}`;
}
